import { getAuth, User } from "firebase/auth";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
  useRouteError,
} from "react-router-dom";
import {
  FormattedTransactionObject,
  TransactionListFiltersType,
} from "~/types";
import { INTENTS, ROUTES } from "~/utils";
import * as APITypes from "~/API";
import { Button, message, Result } from "antd";
import { apiClient } from "~/services/apiClient";
import type { ReadTransactionReceiptResult } from "~/types/paymentReceipt";

export const PaymentTablePageLoader = async ({
  request,
}: LoaderFunctionArgs) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return redirect(ROUTES.home);
  const { searchParams } = new URL(request.url);

  const status = searchParams.get("status") as
    | APITypes.PaymentStatusType
    | undefined;
  const response = await apiClient.loader<{
    transactions: FormattedTransactionObject[];
    filters: TransactionListFiltersType;
  }>("payment-table", {
    status: status ?? undefined,
  });

  if (!response.success || !response.data) {
    return {
      currentUser: user,
      transactions: [],
      filters: { status },
    };
  }

  return {
    currentUser: user,
    transactions: response.data.transactions,
    filters: response.data.filters,
  };
};

export type PaymentTablePageLoaderData = {
  currentUser: User | null;
  transactions: FormattedTransactionObject[];
  filters: TransactionListFiltersType;
};

export const PaymentTablePageAction = async ({
  request,
}: ActionFunctionArgs) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return redirect(ROUTES.home);

  const formData = await request.formData();
  const body = formData.get("body") as string;
  const intent = formData.get("intent");

  switch (intent) {
    case INTENTS._terminate_transaction: {
      const payload = JSON.parse(body ?? "{}") as { transactionIDs: string[] };
      const result = await apiClient.sendIntent(INTENTS._terminate_transaction, {
        transactionIDs: payload.transactionIDs ?? [],
      });
      if (result.success) {
        return { success: true };
      }
      message.error(result.error ?? "Failed to terminate transaction(s)");
      return { error: result.error ?? "Failed to terminate transaction(s)" };
    }
    case INTENTS._view_transaction_receipt: {
      const transactionID = formData.get("transactionID") as string;
      const language = (formData.get("language") as string | null)?.trim();
      if (!transactionID?.trim()) {
        return { success: false, error: "transactionID is required" };
      }
      const result = await apiClient.loader<ReadTransactionReceiptResult>(
        "transaction-receipt",
        {
          transaction_id: transactionID.trim(),
          ...(language ? { language } : {}),
        },
      );
      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error ?? "Failed to load receipt",
        };
      }
      return result.data;
    }
    default:
      return { error: "Invalid intent" };
  }
};

export const PaymentTablePageErrorBoundary = () => {
  const error = useRouteError();
  console.error("Payment table page error", error);
  return (
    <Result status="500" title="500" subTitle="Sorry, something went wrong.">
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button type="primary" href="/">
          Back Home
        </Button>
      </div>
    </Result>
  );
};
